/**
 * ArcSkillEffect — Lightning tree fork-1 active: chain lightning bolt.
 *
 * A bolt fires at the primary target, then bounces to the nearest secondary
 * target within chain range. Two targets at base (primary + one hop). The
 * bolt consumes all accumulated Static charge on activation, converting it
 * to bonus damage on the primary hit via chargeBonusDamageRatio.
 *
 * Arc does NOT apply stun — it is a pure damage chain. CC in the arc path
 * arrives from Spark/Jolt up the trunk and Conduit's relay mechanic further
 * down the line.
 *
 * Upgrade keys consumed via UpgradeEffectResolver (scoped to 'arc'):
 * - `chainTargets` (additive) — +1 from Forked (more chain hops)
 * - `chainDamageRetention` (multiplicative) — 0.85 from Forked (per-hop falloff)
 * - `stunnedNodeChainRangeMultiplier` (additive) — ×2 from Conduit (range bonus)
 * - `chainThroughStunned` (boolean) — from Conduit (relay condition)
 *
 * Fizzle: cooldown consumed, no damage, no chain.
 * Out-of-range: no cooldown consumed.
 *
 * Chain origin is the primary target's position, not the player's.
 *
 * @see ADR-064 (attunement shared systems architecture)
 * @see lightning-spec.md § Arc
 */

import type { SkillEffect } from '../../SkillEffect.js';

// ---------------------------------------------------------------------------
// Dependency interfaces
// ---------------------------------------------------------------------------

export interface ArcChainConfig {
  readonly originX: number;
  readonly originY: number;
  readonly maxTargets: number;
  readonly maxRangeTiles: number;
  readonly damageRetention: number;
  readonly mode: 'single';
  readonly bonusRangeForStatus?: {
    readonly statusId: string;
    readonly rangeMultiplier: number;
  };
  readonly relayCondition?: {
    readonly statusId: string;
  };
}

export interface ArcChainResult {
  readonly targetId: string;
  readonly damageMultiplier: number;
}

export interface ArcDeps {
  readonly resolver: {
    getAdditiveEffect(skillId: string, key: string): number;
    getMultiplicativeEffect(skillId: string, key: string): number;
    hasEffect(skillId: string, key: string): boolean;
  };
  /** Consume all charge for the given ID. Returns amount consumed. */
  readonly consumeCharge: (chargeId: string) => number;
  /**
   * Find chain targets from a starting position. Returns ordered targets
   * with accumulated damage multipliers. The integration layer wires this
   * to ChainTargetingSystem.findChainTargets with real enemy candidates.
   */
  readonly findChainTargets: (config: ArcChainConfig) => ArcChainResult[];
  /** Returns entity ID of nearest enemy within range, or null if none. */
  readonly getTarget: (rangeTiles: number) => string | null;
  /** Returns position of target entity (used as chain origin). */
  readonly getTargetPosition: (targetId: string) => { x: number; y: number };
  /** Returns true if the skill activation succeeds (false = fizzle). */
  readonly rollSkillSuccess: (skillId: string) => boolean;
  /** Break stealth/hide on activation. */
  readonly breakHide: () => void;
  /** Deal direct (non-DoT) hit damage to a single target. */
  readonly dealDamage: (targetId: string, damage: number) => void;
}

// ---------------------------------------------------------------------------
// Config
// ---------------------------------------------------------------------------

export interface ArcConfig {
  /** Cooldown in ms after activation or fizzle. Default: 800 (medium tier). */
  readonly cooldownMs: number;
  /** Base bolt damage before static charge bonus. */
  readonly baseDamage: number;
  /** Base bolt range in tiles. Default: 3 (medium range). */
  readonly baseRangeTiles: number;
  /** Base chain hop range in tiles. */
  readonly baseChainRangeTiles: number;
  /** Ratio of consumed charge converted to bonus damage. Default: 0.1. */
  readonly chargeBonusDamageRatio: number;
  /** Charge ID to consume from ChargeSystem. Default: 'static_charge'. */
  readonly chargeId: string;
}

const DEFAULT_CONFIG: ArcConfig = {
  cooldownMs: 800,                // medium cooldown tier
  baseDamage: 10,                 // moderate fork-1 damage — value through chain
  baseRangeTiles: 3,              // medium bolt range
  baseChainRangeTiles: 2,         // chain hop distance
  chargeBonusDamageRatio: 0.1,    // matches Static passive default
  chargeId: 'static_charge',
};

// ---------------------------------------------------------------------------
// Skill effect
// ---------------------------------------------------------------------------

export class ArcSkillEffect implements SkillEffect {
  readonly skillId = 'arc';
  readonly type = 'active' as const;
  readonly activationMode = 'instant' as const;
  readonly missLabel = 'WHIFF';
  readonly failLabel = 'FIZZLE';

  private readonly deps: ArcDeps;
  private readonly config: ArcConfig;

  private _cooldownRemaining = 0;
  private _activations = 0;
  private _fizzles = 0;
  private _outOfRangeAttempts = 0;
  private _totalChainHits = 0;

  constructor(deps: ArcDeps, config: Partial<ArcConfig> = {}) {
    this.deps = deps;
    this.config = { ...DEFAULT_CONFIG, ...config };
  }

  // --- Observable state ---

  get cooldown(): number { return this._cooldownRemaining; }
  get activations(): number { return this._activations; }
  get fizzles(): number { return this._fizzles; }
  get outOfRangeAttempts(): number { return this._outOfRangeAttempts; }
  get totalChainHits(): number { return this._totalChainHits; }

  // --- SkillEffect interface ---

  canActivate(): boolean {
    return this._cooldownRemaining <= 0;
  }

  activate(): boolean {
    if (!this.canActivate()) return false;

    // Range check — no target = no cooldown consumed
    const targetId = this.deps.getTarget(this.config.baseRangeTiles);
    if (targetId === null) {
      this._outOfRangeAttempts++;
      return false;
    }

    // Fizzle check — cooldown IS consumed on fizzle
    if (!this.deps.rollSkillSuccess('arc')) {
      this._fizzles++;
      this._cooldownRemaining = this.config.cooldownMs;
      return false;
    }

    // Break hide on successful activation
    this.deps.breakHide();

    // Calculate primary damage: base + static charge bonus
    const chargeConsumed = this.deps.consumeCharge(this.config.chargeId);
    const chargeBonusDamage = chargeConsumed * this.config.chargeBonusDamageRatio;
    const primaryDamage = this.config.baseDamage + chargeBonusDamage;

    // Deal primary hit damage
    this.deps.dealDamage(targetId, primaryDamage);

    // Chain targeting from primary hit position (not player position)
    const targetPos = this.deps.getTargetPosition(targetId);

    // Resolve chain parameters from upgrades
    const chainTargets = 1 + this.deps.resolver.getAdditiveEffect('arc', 'chainTargets');
    const damageRetention = this.deps.resolver.getMultiplicativeEffect('arc', 'chainDamageRetention');

    // Conduit: stunned enemies get bonus chain range
    const stunnedRangeMultiplier = this.deps.resolver.getAdditiveEffect('arc', 'stunnedNodeChainRangeMultiplier');
    const bonusRangeForStatus = stunnedRangeMultiplier > 0
      ? { statusId: 'stun', rangeMultiplier: stunnedRangeMultiplier }
      : undefined;

    // Conduit: stunned enemies act as relay nodes
    const chainThroughStunned = this.deps.resolver.hasEffect('arc', 'chainThroughStunned');
    const relayCondition = chainThroughStunned
      ? { statusId: 'stun' }
      : undefined;

    const chainResults = this.deps.findChainTargets({
      originX: targetPos.x,
      originY: targetPos.y,
      maxTargets: chainTargets,
      maxRangeTiles: this.config.baseChainRangeTiles,
      damageRetention,
      mode: 'single',
      bonusRangeForStatus,
      relayCondition,
    });

    // Apply chain hits with accumulated retention multipliers
    for (const result of chainResults) {
      this.deps.dealDamage(result.targetId, primaryDamage * result.damageMultiplier);
      this._totalChainHits++;
    }

    this._activations++;
    this._cooldownRemaining = this.config.cooldownMs;
    return true;
  }

  deactivate(): void {}

  isActive(): boolean { return false; }

  update(delta: number): void {
    if (this._cooldownRemaining > 0) {
      this._cooldownRemaining = Math.max(0, this._cooldownRemaining - delta);
    }
  }

  destroy(): void {
    this._cooldownRemaining = 0;
  }
}
